// Shared Rocket Nozzle field state (Space → Rocket Nozzle).
//
// The nozzle inputs were owned as local component state inside SpaceContent.
// Converting the feature tree to the windowed pattern splits that single pane
// into TWO floating windows — "Nozzle Parameters" (inputs) and "Rocket
// Performance" (computed outputs) — which mount/unmount independently as each
// window opens/closes. To keep ONE source of truth across both (so closing the
// Nozzle Parameters window doesn't reset a value you just typed), the field
// values live in a tiny module-level store, subscribed via
// useSyncExternalStore — the same shape as tubeframeFieldsStore.ts.
//
// Note: these values are a client-side physics PREVIEW/calculator only — the
// actual rocket-nozzle model is generated by the LLM from a fixed text prompt
// (see usePromptSidebarActions.ts), independent of these inputs. Persisting
// them across window open/close is a pure UX nicety, not a generation-
// correctness requirement.

import { useCallback, useSyncExternalStore } from 'react';
import type { InputState } from './rocketNozzle';
import { defaultInputs, type InputKey } from './rocketNozzle';

let state: InputState = { ...defaultInputs };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

function setField(key: InputKey, value: string) {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  emit();
}

/** Reset to the tutorial defaults — called when the rocket-nozzle surface
 *  unmounts, so a module-level store doesn't retain stale values across
 *  navigation/conversations. */
export function resetRocketNozzleFields() {
  state = { ...defaultInputs };
  emit();
}

export interface RocketNozzleFieldsApi {
  inputs: InputState;
  setField: (key: InputKey, value: string) => void;
}

export function useRocketNozzleFields(): RocketNozzleFieldsApi {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    inputs: snapshot,
    setField: useCallback(setField, []),
  };
}
